import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUserTie, FaCode, FaUser, FaRocket, FaBolt, FaCloud, FaLock, FaCheck, FaArrowRight } from 'react-icons/fa';

const BuiltForYou = () => {
  const [activePersona, setActivePersona] = useState(0);

  const personas = [
    {
      icon: <FaUserTie className="text-4xl" />,
      title: 'Business Owners & Entrepreneurs',
      subtitle: 'Take control of your leads and marketing results.',
      benefits: ['See all your leads in one place', 'Instantly identify high-quality leads', 'Track ROI across ads, forms, and campaigns', 'Spend less time guessing, more time closing'],
      color: 'from-pink-500 to-rose-500',
      description: 'Running ads, posting daily, and still unsure where your leads come from? This system organizes everything ,showing you what’s working and what’s not.'
    },
    {
      icon: <FaCode className="text-4xl" />,
      title: 'Admins & Operations Teams',
      subtitle: 'Simplify data chaos and automate the boring stuff.',
      benefits: ['Automatic data syncing & cleaning', 'Detects fake or duplicate leads', 'Generates accurate, easy reports', 'Reduces manual workload significantly'],
      color: 'from-blue-500 to-cyan-500',
      description: 'No more spreadsheets, duplicate entries, or chasing data updates. The system cleans, syncs, and monitors your leads automatically.'
    },
    {
      icon: <FaUser className="text-4xl" />,
      title: 'Marketing Teams & Agencies',
      subtitle: 'Prove what’s working, with real data and clarity.',
      benefits: ['Track every campaign’s impact', 'Eliminate fake traffic or bots', 'Create transparent client reports', 'Strengthen marketing ROI and trust'],
      color: 'from-purple-500 to-indigo-500',
      description: 'Finally, see which campaigns actually convert. Attribute every lead to its true source and get instant performance insights.'
    },
  ];

  const techHighlights = [
    { icon: <FaRocket />, label: 'Stack', detail: 'React, Node, MongoDB, TensorFlow/OpenAI AI' },
    { icon: <FaBolt />, label: 'Performance', detail: '1000 leads/hr, <3s loads, 99.9% uptime SLA' },
    { icon: <FaCloud />, label: 'Deployment', detail: 'Win/macOS/Linux; local, cloud, or hybrid' },
    { icon: <FaLock />, label: 'Security', detail: 'JWT auth, rate limiting, enterprise encryption' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-purple-50 py-32">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-pink-200/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-cyan-200/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative container mx-auto px-8 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200 mb-6"
          >
            <FaRocket className="text-purple-600" />
            <span className="text-sm font-bold text-purple-900">Built for Visionaries</span>
          </motion.div>

          <h2 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
            <span className="block text-slate-900">Built for</span>
            <span className="block bg-gradient-to-r from-pink-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
              Everyone
            </span>
          </h2>
          
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Perfect for teams that want reliable insights without juggling multiple tools. All your data, secure and beautifully visualized.
          </p>
        </motion.div>

        {/* Personas Grid with Interactive Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8 mb-20"
        >
          {personas.map((persona, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              whileHover={{ y: -12, scale: 1.02 }}
              onClick={() => setActivePersona(idx)}
              className="group relative cursor-pointer"
            >
              <div className={`relative h-full bg-white rounded-3xl p-8 border-2 ${
                activePersona === idx ? 'border-purple-300 shadow-2xl' : 'border-slate-200 shadow-lg'
              } hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col`}>
                
                {/* Gradient overlay on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${persona.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-3xl`} />

                {/* Background decoration */}
                <div className={`absolute -bottom-20 -right-20 w-40 h-40 bg-gradient-to-br ${persona.color} opacity-5 rounded-full blur-3xl group-hover:opacity-10 transition-opacity`} />

                <div className="relative z-10 flex flex-col h-full">
                  {/* Icon Container */}
                  <motion.div
                    className={`relative mb-6 w-20 h-20 rounded-2xl bg-gradient-to-br ${persona.color} flex items-center justify-center shadow-xl text-white`}
                    whileHover={{ rotate: 360, scale: 1.15 }}
                    transition={{ duration: 0.6, type: "spring", stiffness: 400 }}
                  >
                    {persona.icon}
                    
                    {/* Active indicator */}
                    {activePersona === idx && (
                      <motion.div
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-green-500 border-2 border-white flex items-center justify-center"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500 }}
                      >
                        <FaCheck className="text-white text-xs" />
                      </motion.div>
                    )}
                  </motion.div>

                  {/* Badge */}
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r ${persona.color} bg-opacity-10 mb-4 w-fit`}>
                    <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${persona.color}`} />
                    <span className="text-xs font-bold text-slate-700">{persona.subtitle}</span>
                  </div>

                  {/* Main content */}
                  <h3 className="text-2xl font-black mb-3 text-slate-900">{persona.title}</h3>

                  {/* Description */}
                  <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow">
                    {persona.description}
                  </p>

                  {/* Benefits list */}
                  <div className="space-y-3 mb-6">
                    {persona.benefits.map((benefit, bIdx) => (
                      <motion.div
                        key={bIdx}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 + bIdx * 0.05 }}
                        className="flex items-center gap-3"
                      >
                        <div className={`p-1.5 bg-gradient-to-br ${persona.color} rounded-lg flex-shrink-0`}>
                          <FaCheck className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-slate-700 text-sm font-medium">{benefit}</span>
                      </motion.div>
                    ))}
                  </div>

                  {/* Bottom accent with progress bar */}
                  <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                      className={`absolute inset-y-0 left-0 bg-gradient-to-r ${persona.color} rounded-full`}
                      initial={{ width: 0 }}
                      whileInView={{ width: activePersona === idx ? "100%" : "0%" }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                  </div>
                </div>

                {/* Corner badge */}
                {activePersona === idx && (
                  <motion.div
                    className="absolute top-6 right-6 px-3 py-1 rounded-full bg-green-500 text-white text-xs font-bold shadow-lg"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    Active
                  </motion.div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>


        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-purple-50 via-pink-50 to-cyan-50 rounded-3xl p-12 border-2 border-purple-200 shadow-xl">
            <p className="text-slate-700 mb-8 text-lg font-medium">
              Pick your role above. We've optimized the experience for you.
            </p>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="group relative px-10 py-5 rounded-2xl font-bold text-lg overflow-hidden shadow-2xl"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 animate-gradient" />
              <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-cyan-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="relative text-white flex items-center gap-3 justify-center">
                Get Started for Free
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <FaArrowRight />
                </motion.div>
              </span>
            </motion.button>
            
            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm">
              {[
                '✓ No credit card',
                '✓ 5-min setup',
                '✓ Cancel anytime'
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 + idx * 0.1 }}
                  className="text-slate-600 font-semibold flex items-center gap-2"
                >
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                    <FaCheck className="text-white text-xs" />
                  </div>
                  {item.replace('✓ ', '')}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default BuiltForYou;